/**
 * SessionMemoryView — HR-BREV-020 Context Orchestration Engine UI
 * 
 * Premium memory browser that lets users semantically search across
 * all archived conversations. This is the #1 differentiator of the
 * HumanReplica.ai platform — sovereign AI memory.
 * 
 * Features:
 * - Semantic search across past conversations
 * - Relevance-scored results with visual indicators
 * - Memory stats dashboard (indexed events, store status)
 * - Animated, glassmorphism-styled interface
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Search, Brain, Database, Activity, Sparkles,
  Clock, ArrowLeft, Loader2, AlertCircle, Zap,
  Star, BookOpen, ChevronRight,
} from 'lucide-react';
import { getApiUrl } from '../lib/env';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = getApiUrl();

// ── Types ──────────────────────────────────────────────────────────────────────

interface MemorySearchResult {
  threadId: string;
  summary: string;
  relevanceScore: number;
  matchedTopics: string[];
  date: string | null;
}

interface MemoryStats {
  replicaId: string;
  embeddingStore: {
    totalEvents: number;
    namespaces: string[];
    isAvailable: boolean;
  };
  status: 'active' | 'fallback' | 'unavailable';
  updatedAt: string;
}

interface SessionMemoryViewProps {
  replicaId: string;
  replicaName: string;
  onBack: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function SessionMemoryView({ replicaId, replicaName, onBack }: SessionMemoryViewProps) {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MemorySearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [stats, setStats] = useState<MemoryStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Load stats on mount
  useEffect(() => {
    (async () => {
      try {
        const token = await user?.getIdToken?.();
        const res = await fetch(`${API_BASE}/memory/${encodeURIComponent(replicaId)}/stats`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setStats(data);
        }
      } catch {
        // Stats are optional — silently ignore
      }
    })();
  }, [replicaId, user]);

  // Search handler
  const handleSearch = useCallback(async () => {
    if (!query.trim() || query.trim().length < 2) return;

    setIsSearching(true);
    setError(null);
    setHasSearched(true);

    try {
      const token = await user?.getIdToken?.();
      const res = await fetch(
        `${API_BASE}/memory/${encodeURIComponent(replicaId)}/search?q=${encodeURIComponent(query.trim())}&limit=15`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      if (!res.ok) {
        throw new Error(`Search failed (${res.status})`);
      }

      const data = await res.json();
      setResults(data.results || []);
    } catch (err: any) {
      setError(err?.message || 'Search failed');
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [query, replicaId, user]);

  // Search on Enter
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  // Relevance color
  const getRelevanceColor = (score: number): string => {
    if (score >= 0.8) return '#22c55e'; // Green — high relevance
    if (score >= 0.5) return '#f59e0b'; // Amber — medium
    return '#94a3b8';                   // Slate — low
  };

  const getRelevanceLabel = (score: number): string => {
    if (score >= 0.8) return 'High';
    if (score >= 0.5) return 'Medium';
    return 'Low';
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-hig-bg-primary">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-chat-border bg-hig-bg-secondary/50 backdrop-blur-sm">
        <button
          onClick={onBack}
          className="p-2 rounded-lg text-hig-text-secondary hover:text-hig-text-primary hover:bg-chat-hover transition-colors"
          title="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.2), rgba(99,102,241,0.15))' }}>
            <Brain size={16} className="text-purple-400" />
          </div>
          <div>
            <h2 className="text-[15px] font-semibold text-hig-text-primary">Memory Browser</h2>
            <p className="text-[11px] text-hig-text-tertiary">{replicaName}'s archived conversations</p>
          </div>
        </div>
      </div>

      {/* ── Main Content ────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-8">

          {/* ── Hero Section ──────────────────────────────────────────── */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
              style={{
                background: 'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(99,102,241,0.1))',
                border: '1px solid rgba(139,92,246,0.2)',
                boxShadow: '0 0 40px rgba(139,92,246,0.08)',
              }}>
              <Brain size={28} className="text-purple-400" />
            </div>
            <h1 className="text-2xl font-bold text-hig-text-primary mb-2">
              Sovereign Memory
            </h1>
            <p className="text-[14px] text-hig-text-secondary max-w-md mx-auto leading-relaxed">
              Search across every archived conversation. Your Replica remembers what matters — 
              preferences, decisions, boundaries, and context.
            </p>
          </div>

          {/* ── Search Bar ────────────────────────────────────────────── */}
          <div className="relative mb-6">
            <div className="flex items-center gap-2 px-4 py-3 rounded-2xl border border-chat-border bg-chat-input transition-all focus-within:border-purple-500/50 focus-within:shadow-[0_0_0_3px_rgba(139,92,246,0.1)]">
              <Search size={18} className="text-hig-text-tertiary flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search memories... (e.g., 'my budget preferences', 'that meeting last week')"
                className="flex-1 bg-transparent text-[15px] text-hig-text-primary placeholder:text-hig-text-tertiary outline-none"
              />
              <button
                onClick={handleSearch}
                disabled={isSearching || query.trim().length < 2}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-medium transition-all disabled:opacity-30"
                style={{
                  background: 'linear-gradient(135deg, rgba(139,92,246,0.2), rgba(99,102,241,0.15))',
                  color: '#a78bfa',
                  border: '1px solid rgba(139,92,246,0.25)',
                }}
              >
                {isSearching ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Sparkles size={14} />
                )}
                <span className="hidden sm:inline">Search</span>
              </button>
            </div>
          </div>

          {/* ── Stats Cards ───────────────────────────────────────────── */}
          {stats && (
            <div className="grid grid-cols-3 gap-3 mb-8">
              {[
                {
                  icon: Database,
                  label: 'Indexed Events',
                  value: stats.embeddingStore.totalEvents.toString(),
                  color: '#8b5cf6',
                  bg: 'rgba(139,92,246,0.1)',
                },
                {
                  icon: Activity,
                  label: 'Store Status',
                  value: stats.status === 'active' ? 'Active' : stats.status === 'fallback' ? 'Keyword' : 'Offline',
                  color: stats.status === 'active' ? '#22c55e' : stats.status === 'fallback' ? '#f59e0b' : '#ef4444',
                  bg: stats.status === 'active' ? 'rgba(34,197,94,0.1)' : stats.status === 'fallback' ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)',
                },
                {
                  icon: Zap,
                  label: 'Search Mode',
                  value: stats.embeddingStore.isAvailable ? 'Semantic' : 'Keyword',
                  color: stats.embeddingStore.isAvailable ? '#6366f1' : '#94a3b8',
                  bg: stats.embeddingStore.isAvailable ? 'rgba(99,102,241,0.1)' : 'rgba(148,163,184,0.1)',
                },
              ].map((card) => (
                <div
                  key={card.label}
                  className="flex flex-col items-center gap-1.5 px-4 py-3 rounded-xl border border-chat-border transition-all hover:border-chat-border-subtle"
                  style={{ background: card.bg }}
                >
                  <card.icon size={16} style={{ color: card.color }} />
                  <span className="text-[16px] font-bold text-hig-text-primary">{card.value}</span>
                  <span className="text-[10px] text-hig-text-tertiary">{card.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* ── Error Banner ──────────────────────────────────────────── */}
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 mb-6">
              <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
              <p className="text-[13px] text-red-300">{error}</p>
            </div>
          )}

          {/* ── Search Results ────────────────────────────────────────── */}
          {hasSearched && !isSearching && results.length === 0 && (
            <div className="text-center py-12">
              <BookOpen size={32} className="mx-auto mb-3 text-hig-text-tertiary opacity-50" />
              <p className="text-[15px] text-hig-text-secondary mb-1">No memories found</p>
              <p className="text-[13px] text-hig-text-tertiary">
                Try a different search term, or chat more with your Replica to build memory.
              </p>
            </div>
          )}

          {results.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[13px] text-hig-text-secondary">
                  Found <span className="font-semibold text-hig-text-primary">{results.length}</span> memories
                </p>
                <p className="text-[11px] text-hig-text-tertiary">
                  Sorted by relevance
                </p>
              </div>

              {results.map((result, idx) => (
                <div
                  key={`${result.threadId}-${idx}`}
                  className="group flex items-start gap-4 px-4 py-3 rounded-xl border border-chat-border bg-chat-hover/30 hover:bg-chat-hover hover:border-chat-border-subtle transition-all"
                  style={{
                    borderLeftWidth: '3px',
                    borderLeftColor: getRelevanceColor(result.relevanceScore),
                  }}
                >
                  {/* Relevance Badge */}
                  <div className="flex flex-col items-center gap-0.5 pt-0.5 flex-shrink-0 min-w-[42px]">
                    <Star size={14} style={{ color: getRelevanceColor(result.relevanceScore) }} />
                    <span
                      className="text-[10px] font-bold"
                      style={{ color: getRelevanceColor(result.relevanceScore) }}
                    >
                      {Math.round(result.relevanceScore * 100)}%
                    </span>
                    <span className="text-[9px] text-hig-text-tertiary">
                      {getRelevanceLabel(result.relevanceScore)}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] text-hig-text-primary leading-relaxed mb-1">
                      {result.summary}
                    </p>
                    <div className="flex items-center gap-3">
                      {result.date && (
                        <span className="flex items-center gap-1 text-[11px] text-hig-text-tertiary">
                          <Clock size={10} />
                          {new Date(result.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                      )}
                      {result.matchedTopics.length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap">
                          {result.matchedTopics.slice(0, 3).map((topic) => (
                            <span
                              key={topic}
                              className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20"
                            >
                              {topic}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Arrow */}
                  <ChevronRight
                    size={14}
                    className="text-hig-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1"
                  />
                </div>
              ))}
            </div>
          )}

          {/* ── Loading State ─────────────────────────────────────────── */}
          {isSearching && (
            <div className="flex flex-col items-center py-12">
              <div className="relative mb-4">
                <div className="w-12 h-12 rounded-full border-2 border-purple-500/20 border-t-purple-500 animate-spin" />
                <Brain size={20} className="absolute inset-0 m-auto text-purple-400" />
              </div>
              <p className="text-[14px] text-hig-text-secondary">Searching memories...</p>
              <p className="text-[12px] text-hig-text-tertiary mt-1">
                Scanning archived conversations for semantic matches
              </p>
            </div>
          )}

          {/* ── Empty State (before first search) ─────────────────────── */}
          {!hasSearched && (
            <div className="mt-4">
              <p className="text-[12px] text-hig-text-tertiary text-center mb-4">
                Try searching for:
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {[
                  'my preferences',
                  'decisions I made',
                  'important boundaries',
                  'project updates',
                  'feedback given',
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => {
                      setQuery(suggestion);
                      // Auto-search after setting
                      setTimeout(() => {
                        inputRef.current?.focus();
                      }, 50);
                    }}
                    className="px-3 py-1.5 rounded-full border border-chat-border text-[12px] text-hig-text-secondary hover:text-hig-text-primary hover:bg-chat-hover hover:border-purple-500/30 transition-all"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
